import {
    computed,
    Directive,
    effect,
    ElementRef,
    forwardRef,
    HostListener,
    inject,
    input,
    signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { clamp } from 'lodash-es';
import { ArrowBehaviours, MaskConfigOptions, Separators } from './mask-options';
import {
    buildSegmentsFromMaskViewModel,
    clampOrWrap,
    isDigit,
    isPlaceholder,
    MaskToken,
    monthOrDayValidator,
    normalizeToMaskTokens,
    pad2,
    pad4,
    quantifyMonthNumbOfDays,
    Seg,
    TokenSeg,
    yyToYYYY,
} from './mask-utils';

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[dateMask]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MaskDirective),
            multi: true,
        },
    ],
})
export class MaskDirective implements ControlValueAccessor {
    private readonly elRef = inject(ElementRef);
    inputElement: HTMLInputElement = this.elRef.nativeElement;

    readonly maskOptions = input.required<MaskConfigOptions>();
    readonly ready = input.required<boolean>();

    readonly dateSeparator = computed<Separators>(() => this.maskOptions().dateSeparator ?? Separators.dash);
    readonly dateTimeSeparator = computed<Separators>(() => this.maskOptions().dateTimeSeparator ?? Separators.space);
    readonly timeSeparator = computed<Separators>(() => this.maskOptions().timeSeparator ?? Separators.colon);

    readonly maxYear = computed<number | null>(() => this.maskOptions().maxYear ?? null);
    readonly minYear = computed<number | null>(() => this.maskOptions().minYear ?? null);

    readonly arrowBehaviours = computed<ArrowBehaviours>(
        () => this.maskOptions().arrowBehaviours ?? ArrowBehaviours.limited_with_control,
    );

    readonly dateConfiguration = computed<string>(() => this.maskOptions().dateConfiguration ?? '');
    readonly timeConfiguration = computed<string>(() => this.maskOptions().timeConfiguration ?? '');

    readonly hasDate = computed<boolean>(() => !!this.dateConfiguration());
    readonly hasTime = computed<boolean>(() => !!this.timeConfiguration());

    readonly timeOnlyMode = computed<boolean>(() => this.hasTime() && !this.hasDate());

    readonly modelValue = signal<string | null>(null);
    private readonly pendingView = signal<string>(''); // ultima view calcolata anche quando ready=false

    /** what is actually displayed (can be partial even when model is null) */
    readonly viewValue = signal<string>('');

    /** The actual view mask shown (placeholders + literals) */
    readonly viewModel = computed<string>(() => {
        const dateTokens = normalizeToMaskTokens(this.dateConfiguration());
        const timeTokens = normalizeToMaskTokens(this.timeConfiguration());

        const ds = this.dateSeparator();
        const ts = this.timeSeparator();
        const dts = this.dateTimeSeparator();

        let out = '';
        if (dateTokens.length) out += dateTokens.join(ds);
        if (dateTokens.length && timeTokens.length) out += dts;
        if (timeTokens.length) out += timeTokens.join(ts);

        return out;
    });

    readonly segments = computed<Seg[]>(() => buildSegmentsFromMaskViewModel(this.viewModel()));
    readonly tokenSegments = computed<TokenSeg[]>(() =>
        this.segments().filter((s): s is TokenSeg => s.kind === 'token'),
    );

    /** View is always derived from model (when model exists) */
    readonly formattedValue = computed<string>(() => this.formatModelToView(this.modelValue()));

    private activated = false;

    private onChange: (v: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor() {
        effect(() => {
            if (!this.ready()) return;

            const view = this.pendingView() || this.viewModel();
            if (this.inputElement.value !== view) {
                this.inputElement.value = view;
            }

            if (!this.activated) {
                this.moveCursorToStartPosition();
                this.activated = true;
            }
        });
    }

    writeValue(value: any): void {
        const model = typeof value === 'string' ? value : null;
        this.modelValue.set(model);

        const view = this.formatModelToView(model);
        this.pendingView.set(view);

        if (!this.ready()) return; // ✅ non tocca il DOM

        if (this.inputElement.value !== view) {
            this.inputElement.value = view;
        }
    }

    registerOnChange(fn: (v: string | null) => void): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.inputElement.disabled = isDisabled;
    }

    // -----------------------------
    // Formatting / Parsing
    // -----------------------------

    private formatModelToView(modelValue: string | null): string {
        const vm = this.viewModel();
        if (!modelValue) return vm;

        const normalized = modelValue.replace(/\s+/, 'T');
        const hasDate = this.hasDate();
        const hasTime = this.hasTime();

        let datePart = '';
        let timePart = '';

        if (hasDate && hasTime) {
            [datePart, timePart] = normalized.split('T');
        } else if (hasDate) {
            datePart = normalized.split('T')[0] ?? '';
        } else if (hasTime) {
            timePart = normalized.split('T')[1] ?? normalized;
        }

        // --- IMPORTANT: keep both y2 and y4
        let yRaw = '';
        let y4 = '';
        let y2 = '';
        let MM = '';
        let dd = '';
        let HH = '';
        let mm = '';
        let ss = '';

        if (datePart) {
            const [y, m, d] = datePart.split('-');
            yRaw = (y ?? '').trim();
            MM = (m ?? '').padStart(2, '0');
            dd = (d ?? '').padStart(2, '0');

            if (yRaw.length <= 2) {
                y2 = yRaw.padStart(2, '0');
                // per yyyy in view, se mai capitasse, espando con pivot
                const full = yyToYYYY(Number(y2));
                y4 = Number.isFinite(full) ? String(full) : y2.padStart(4, '0');
            } else {
                y4 = yRaw.padStart(4, '0');
                y2 = y4.slice(-2);
            }
        }

        if (timePart) {
            const [h, mi, s] = timePart.split(':');
            HH = (h ?? '').padStart(2, '0');
            mm = (mi ?? '').padStart(2, '0');
            ss = (s ?? '').padStart(2, '0');
        }

        let out = '';
        for (const seg of this.segments()) {
            if (seg.kind === 'lit') {
                out += seg.value;
                continue;
            }
            switch (seg.token) {
                case 'dd':
                    out += dd || 'dd';
                    break;
                case 'MM':
                    out += MM || 'MM';
                    break;
                case 'yyyy':
                    out += y4 || 'yyyy';
                    break;
                case 'yy':
                    out += y2 || 'yy';
                    break;
                case 'HH':
                case 'hh':
                    out += HH || seg.token;
                    break;
                case 'mm':
                    out += mm || 'mm';
                    break;
                case 'ss':
                    out += ss || 'ss';
                    break;
            }
        }
        return out;
    }

    private parseViewToModel(viewValue: string): string | null {
        const tokenSegs = this.tokenSegments();
        const vm = this.viewModel();
        if (!vm) return null;

        if (!viewValue) return null;
        if (viewValue.length < vm.length) return null;

        const getTok = (t: MaskToken): string => {
            const seg = tokenSegs.find((s) => s.token === t || (t === 'yyyy' && s.token === 'yy'));
            if (!seg) return '';
            return viewValue.slice(seg.start, seg.end);
        };

        const hasDate = this.hasDate();
        const hasTime = this.hasTime();

        // date pieces
        const rawDD = hasDate ? getTok('dd') : '';
        const rawMM = hasDate ? getTok('MM') : '';
        const rawYYYY = hasDate ? getTok('yyyy') : '';
        const rawYY = hasDate ? getTok('yy') : '';

        // time pieces
        const rawHH = hasTime ? getTok('HH') || getTok('hh') : '';
        const rawMin = hasTime ? getTok('mm') : '';
        const hasSeconds = tokenSegs.some((s) => s.token === 'ss');
        const rawSec = hasTime ? (hasSeconds ? getTok('ss') : '00') : '';

        // placeholders => null
        if (hasDate) {
            const y = rawYYYY || rawYY;
            if (isPlaceholder(rawDD) || isPlaceholder(rawMM) || isPlaceholder(y)) return null;
        }
        if (hasTime) {
            if (isPlaceholder(rawHH) || isPlaceholder(rawMin) || (hasSeconds && isPlaceholder(rawSec))) return null;
        }

        // ----- YEAR POLICY (KEY CHANGE)
        const hasYYYYToken = tokenSegs.some((s) => s.token === 'yyyy');
        const hasYYToken = tokenSegs.some((s) => s.token === 'yy') && !hasYYYYToken;

        // year used for validation (always 4-digit when date exists)
        let validationYYYY = '';
        // year used for output (either 4-digit or 2-digit depending on token)
        let outYear = '';

        if (hasDate) {
            if (rawYYYY) {
                const y4 = pad4(rawYYYY);
                validationYYYY = y4;
                outYear = hasYYYYToken ? y4 : y4.slice(-2); // in case someone mixes tokens
            } else {
                // only yy in view
                const yyNum = Number(rawYY);
                if (!Number.isFinite(yyNum)) return null;

                // output stays 2-digit
                outYear = pad2(String(yyNum));

                // validation uses pivot expansion
                const full = yyToYYYY(yyNum);
                if (!Number.isFinite(full)) return null;
                validationYYYY = String(full);
            }
        }

        const DD = hasDate ? pad2(rawDD) : '';
        const MM = hasDate ? pad2(rawMM) : '';

        const HH = hasTime ? pad2(rawHH) : '';
        const MIN = hasTime ? pad2(rawMin) : '';
        const SEC = hasTime ? pad2(hasSeconds ? rawSec : '00') : '';

        // validate date if present (use 4-digit year)
        if (hasDate && !monthOrDayValidator(DD, MM, validationYYYY)) return null;

        // ----- OUTPUT
        // If mask has only yy (no yyyy), year is 2-digit in model.
        // Otherwise it's 4-digit.
        const Y = hasYYToken ? outYear : validationYYYY;

        if (hasDate && !hasTime) return `${Y}-${MM}-${DD}`;
        if (!hasDate && hasTime) return `${HH}:${MIN}:${SEC}`;
        return `${Y}-${MM}-${DD}T${HH}:${MIN}:${SEC}`;
    }

    private emitModelFromView(): void {
        const view = this.inputElement.value;
        this.viewValue.set(view); // ✅ keep partial view

        const model = this.parseViewToModel(view);

        // ✅ modelValue: tienilo come "last external model" se vuoi,
        // ma non usarlo per rigenerare la view (ora la view viene da viewValue)
        this.modelValue.set(model);

        this.onChange(model);
    }

    // -----------------------------
    // Caret / Token utilities
    // -----------------------------

    private getTokenSegAtCaret(): TokenSeg | null {
        const segs = this.tokenSegments();
        if (!segs.length) return null;

        const start = this.inputElement.selectionStart ?? 0;
        const end = this.inputElement.selectionEnd ?? start;
        let pos = Math.floor((start + end) / 2);

        pos = clamp(pos, 0, this.viewModel().length - 1);

        // If caret is on a literal segment, choose nearest right token, else left.
        const hit = segs.find((s) => pos >= s.start && pos < s.end);
        if (hit) return hit;

        const right = segs.find((s) => s.start >= pos);
        if (right) return right;

        for (let i = segs.length - 1; i >= 0; i--) {
            if (segs[i].end <= pos) return segs[i];
        }

        return segs[0];
    }

    private focusTokenSeg(seg: TokenSeg): void {
        this.inputElement.setSelectionRange(seg.start, seg.end);
    }

    private nextTokenSeg(seg: TokenSeg, prev = false): TokenSeg | null {
        const segs = this.tokenSegments();
        const i = segs.indexOf(seg);
        if (i < 0) return null;
        const j = prev ? i - 1 : i + 1;
        return segs[j] ?? null;
    }

    private moveCursorToStartPosition(): void {
        const segs = this.tokenSegments();
        const value = this.inputElement.value || this.viewModel();

        for (const seg of segs) {
            const v = value.slice(seg.start, seg.end);
            if (isPlaceholder(v)) {
                this.focusTokenSeg(seg);
                return;
            }
        }

        // if all filled, focus last
        const last = segs[segs.length - 1];
        if (last) this.focusTokenSeg(last);
    }

    // -----------------------------
    // Validation helpers
    // -----------------------------

    private tokenRange(seg: TokenSeg, currentParts: string[]): { min: number; max: number; width: 2 | 4 } {
        const segs = this.tokenSegments();

        const getPart = (token: MaskToken) => {
            const idx = segs.findIndex((s) => s.token === token);
            return idx >= 0 ? currentParts[idx] : '';
        };

        const yearStr = getPart('yyyy') || getPart('yy');
        const monthStr = getPart('MM');

        let year = Number(yearStr);
        const month = Number(monthStr);

        // ✅ if view uses yy, expand to YYYY for leap-year/day calc
        const hasYYYY = segs.some((s) => s.token === 'yyyy');
        if (!hasYYYY) {
            // only yy in mask (or yy is the effective year)
            if (!isPlaceholder(yearStr) && yearStr.length <= 2 && Number.isFinite(year)) {
                const full = yyToYYYY(year);
                if (Number.isFinite(full)) year = full;
            }
        }

        switch (seg.type) {
            case 'day': {
                const maxDay =
                    Number.isFinite(month) && Number.isFinite(year) ? quantifyMonthNumbOfDays(month, year) : 31;
                return { min: 1, max: maxDay, width: 2 };
            }
            case 'month':
                return { min: 1, max: 12, width: 2 };
            case 'year': {
                const minY = this.minYear();
                const maxY = this.maxYear();
                return { min: minY ?? -9999, max: maxY ?? 9999, width: seg.width };
            }
            case 'hour24':
                return { min: 0, max: 23, width: 2 };
            case 'hour12':
                return { min: 1, max: 12, width: 2 };
            case 'minute':
            case 'second':
                return { min: 0, max: 59, width: 2 };
        }
    }

    private getTokenPartsFromView(): string[] {
        const segs = this.tokenSegments();
        const v = this.inputElement.value || this.viewModel();
        return segs.map((s) => v.slice(s.start, s.end));
    }

    private setViewFromTokenParts(parts: string[]): void {
        const segs = this.segments();

        let t = 0;
        let out = '';
        for (const seg of segs) {
            if (seg.kind === 'lit') {
                out += seg.value;
            } else {
                out += parts[t] ?? seg.token;
                t++;
            }
        }

        // keep value length aligned
        if (out.length !== this.viewModel().length) {
            // as a guard, fall back to viewModel if broken
            this.inputElement.value = this.viewModel();
            return;
        }

        this.inputElement.value = out;
        this.viewValue.set(out); // se stai usando viewValue
        // NON toccare selectionStart/End qui
    }

    private resetSegToPlaceholder(parts: string[], seg: TokenSeg): void {
        const idx = this.tokenSegments().indexOf(seg);
        if (idx >= 0) parts[idx] = seg.token;
    }

    // -----------------------------
    // Editing
    // -----------------------------

    /**
     * writeDigit UX (mask-classic):
     * - se token è placeholder (contiene lettere) -> riempi progressivamente da sinistra: dd -> 3d -> 31
     * - se token è tutto цифre e token selezionato -> overwrite progressivo: 31 selezionato + '0' -> 0d (non 00) e poi 05 ecc.
     * - se token è tutto цифre e NON selezionato -> shift: 19 + 1 => 91 (opzionale ma utile)
     *
     * Quando il token è incompleto (contiene ancora placeholder), NON avanzo al prossimo token.
     * Avanzo solo quando il token è completo (tutto цифre).
     */
    private writeDigit(seg: TokenSeg, digit: string): void {
        const tokenSegs = this.tokenSegments();
        const idx = tokenSegs.indexOf(seg);
        if (idx < 0) return;

        const parts = this.getTokenPartsFromView();
        const token = seg.token; // 'dd'/'MM'/'yy'/'yyyy'
        const width = seg.width; // 2 o 4
        const cur = parts[idx] ?? token;

        const selStart = this.inputElement.selectionStart ?? seg.start;
        const selEnd = this.inputElement.selectionEnd ?? selStart;
        const hasSelection = selEnd > selStart;

        const tokenFullySelected = hasSelection && selStart <= seg.start && selEnd >= seg.end;
        const selectionIntersects = hasSelection && Math.max(selStart, seg.start) < Math.min(selEnd, seg.end);

        const isComplete = (v: string) => !isPlaceholder(v) && v.length === width;

        // 1) Se è placeholder (dd -> 3d -> 31)
        if (isPlaceholder(cur)) {
            const chars = cur.split('');
            const p = chars.findIndex((c) => /[a-zA-Z]/.test(c));
            if (p >= 0) chars[p] = digit;

            const next = chars.join('');
            parts[idx] = next;
            this.setViewFromTokenParts(parts);

            if (!isComplete(next)) {
                const nextPos = seg.start + Math.min(p + 1, width - 1);
                this.inputElement.setSelectionRange(nextPos, nextPos + 1);
                return;
            }

            if (!this.validateAfterTokenComplete(seg, parts)) {
                this.resetSegToPlaceholder(parts, seg);
                this.setViewFromTokenParts(parts);
                this.focusTokenSeg(seg);
                return;
            }

            const nxt = this.nextTokenSeg(seg);
            if (nxt) this.focusTokenSeg(nxt);
            else this.focusTokenSeg(seg);
            return;
        }

        // 2) Se token completo e:
        //    - selezionato/interseca selezione
        //    - OPPURE anche senza selezione: restart progressive (✅ fix per MM 01 -> 0M -> 02)
        if (tokenFullySelected || selectionIntersects || isComplete(cur)) {
            const reset = token.split('');
            // primo carattere = digit, il resto resta placeholder (MM -> 0M)
            reset[0] = digit;
            const next = reset.join('');

            parts[idx] = next;
            this.setViewFromTokenParts(parts);

            if (!isComplete(next)) {
                const nextPos = seg.start + 1;
                this.inputElement.setSelectionRange(nextPos, nextPos + 1);
                return;
            }

            if (!this.validateAfterTokenComplete(seg, parts)) {
                this.resetSegToPlaceholder(parts, seg);
                this.setViewFromTokenParts(parts);
                this.focusTokenSeg(seg);
                return;
            }

            const nxt = this.nextTokenSeg(seg);
            if (nxt) this.focusTokenSeg(nxt);
            else this.focusTokenSeg(seg);
            return;
        }
    }

    /**
     * Valida range + coerenza data SOLO quando il token è completo (nessun placeholder).
     * Ritorna true se ok, false se va reset.
     */
    private validateAfterTokenComplete(seg: TokenSeg, parts: string[]): boolean {
        const { min, max } = this.tokenRange(seg, parts);
        const idx = this.tokenSegments().indexOf(seg);
        const n = Number(parts[idx]);

        if (!Number.isFinite(n) || n < min || n > max) return false;

        if (this.hasDate()) {
            const tokenSegs = this.tokenSegments();
            const dIdx = tokenSegs.findIndex((s) => s.token === 'dd');
            const mIdx = tokenSegs.findIndex((s) => s.token === 'MM');
            const y4Idx = tokenSegs.findIndex((s) => s.token === 'yyyy');
            const y2Idx = tokenSegs.findIndex((s) => s.token === 'yy');

            if (dIdx >= 0 && mIdx >= 0 && (y4Idx >= 0 || y2Idx >= 0)) {
                const dd = parts[dIdx] ?? 'dd';
                const MM = parts[mIdx] ?? 'MM';

                let yyyy = '';
                if (y4Idx >= 0) {
                    yyyy = parts[y4Idx] ?? 'yyyy';
                } else {
                    const yy = parts[y2Idx] ?? 'yy';
                    if (!isPlaceholder(yy)) {
                        const full = yyToYYYY(Number(yy));
                        if (Number.isFinite(full)) yyyy = String(full);
                    } else {
                        yyyy = 'yyyy'; // placeholder
                    }
                }

                // ✅ lazy calendar validation
                if (!isPlaceholder(dd) && !isPlaceholder(MM) && !isPlaceholder(yyyy)) {
                    if (!monthOrDayValidator(dd, MM, yyyy)) return false;
                }
            }
        }

        return true;
    }

    private bumpToken(seg: TokenSeg, delta: number): void {
        const behaviour = this.arrowBehaviours();
        const tokenSegs = this.tokenSegments();
        const idx = tokenSegs.indexOf(seg);
        if (idx < 0) return;

        const parts = this.getTokenPartsFromView();
        const curStr = parts[idx] ?? seg.token;
        const curNum = isPlaceholder(curStr) ? NaN : Number(curStr);

        const { min, max, width } = this.tokenRange(seg, parts);

        // --- circular_without_position: carry on date only
        if (behaviour === ArrowBehaviours.circular_without_position && this.hasDate()) {
            if (seg.type === 'day' || seg.type === 'month' || seg.type === 'year') {
                const dIdx = tokenSegs.findIndex((s) => s.type === 'day');
                const mIdx = tokenSegs.findIndex((s) => s.type === 'month');
                const yIdx = tokenSegs.findIndex((s) => s.token === 'yyyy' || s.token === 'yy');

                const dayStr = dIdx >= 0 ? parts[dIdx] : 'dd';
                const monStr = mIdx >= 0 ? parts[mIdx] : 'MM';
                const yeaStr = yIdx >= 0 ? parts[yIdx] : 'yyyy';

                let day = isPlaceholder(dayStr) ? 1 : Number(dayStr);
                let mon = isPlaceholder(monStr) ? 1 : Number(monStr);

                let yea = 2000;
                if (!isPlaceholder(yeaStr)) {
                    if (tokenSegs[yIdx]?.token === 'yy') {
                        const full = yyToYYYY(Number(yeaStr));
                        yea = Number.isFinite(full) ? full : new Date().getFullYear();
                    } else {
                        yea = Number(yeaStr);
                    }
                } else {
                    yea = this.minYear() ?? new Date().getFullYear();
                }

                if (seg.type === 'day') day += delta;
                if (seg.type === 'month') mon += delta;
                if (seg.type === 'year') yea += delta;

                // normalize month
                if (mon > 12) {
                    mon = 1;
                    yea += 1;
                }
                if (mon < 1) {
                    mon = 12;
                    yea -= 1;
                }

                // normalize day
                let maxDays = quantifyMonthNumbOfDays(mon, yea);
                if (day > maxDays) {
                    day = 1;
                    mon += 1;
                    if (mon > 12) {
                        mon = 1;
                        yea += 1;
                    }
                }
                if (day < 1) {
                    mon -= 1;
                    if (mon < 1) {
                        mon = 12;
                        yea -= 1;
                    }
                    maxDays = quantifyMonthNumbOfDays(mon, yea);
                    day = maxDays;
                }

                // clamp optional year bounds
                const minY = this.minYear();
                const maxY = this.maxYear();
                if (minY != null && yea < minY) {
                    yea = minY;
                    mon = 1;
                    day = 1;
                }
                if (maxY != null && yea > maxY) {
                    yea = maxY;
                    mon = 12;
                    day = quantifyMonthNumbOfDays(mon, yea);
                }

                if (dIdx >= 0) parts[dIdx] = String(day).padStart(2, '0');
                if (mIdx >= 0) parts[mIdx] = String(mon).padStart(2, '0');
                if (yIdx >= 0) {
                    if (tokenSegs[yIdx].token === 'yy') parts[yIdx] = String(yea % 100).padStart(2, '0');
                    else parts[yIdx] = String(yea).padStart(4, '0');
                }

                this.setViewFromTokenParts(parts);
                this.focusTokenSeg(seg);
                return;
            }
        }

        // --- circular_with_position_and_control / limited_with_control
        const nextNum = Number.isFinite(curNum) ? curNum + delta : min;

        if (behaviour === ArrowBehaviours.circular_with_position_and_control) {
            const wrapped = clampOrWrap(nextNum, min, max, true);
            parts[idx] = String(wrapped).padStart(width, '0');

            if (this.hasDate()) {
                // validate
                const dIdx = tokenSegs.findIndex((s) => s.token === 'dd');
                const mIdx = tokenSegs.findIndex((s) => s.token === 'MM');
                const yIdx = tokenSegs.findIndex((s) => s.token === 'yyyy' || s.token === 'yy');

                if (dIdx >= 0 && mIdx >= 0 && yIdx >= 0) {
                    const dd = parts[dIdx] ?? 'dd';
                    const MM = parts[mIdx] ?? 'MM';
                    const yv = parts[yIdx] ?? tokenSegs[yIdx].token;

                    let yyyy = yv;
                    if (tokenSegs[yIdx].token === 'yy' && !isPlaceholder(yv)) {
                        const full = yyToYYYY(Number(yv));
                        if (Number.isFinite(full)) yyyy = String(full);
                    }

                    if (!isPlaceholder(yyyy) && !monthOrDayValidator(dd, MM, yyyy)) {
                        this.resetSegToPlaceholder(parts, seg);
                    }
                }
            }

            this.setViewFromTokenParts(parts);
            this.focusTokenSeg(seg);
            return;
        }

        // limited_with_control
        if (nextNum < min || nextNum > max) {
            const nxt = this.nextTokenSeg(seg, delta < 0);
            if (nxt) this.focusTokenSeg(nxt);
            return;
        }

        parts[idx] = String(nextNum).padStart(width, '0');

        if (this.hasDate()) {
            const dIdx = tokenSegs.findIndex((s) => s.token === 'dd');
            const mIdx = tokenSegs.findIndex((s) => s.token === 'MM');
            const yIdx = tokenSegs.findIndex((s) => s.token === 'yyyy' || s.token === 'yy');

            if (dIdx >= 0 && mIdx >= 0 && yIdx >= 0) {
                const dd = parts[dIdx] ?? 'dd';
                const MM = parts[mIdx] ?? 'MM';
                const yv = parts[yIdx] ?? tokenSegs[yIdx].token;

                let yyyy = yv;
                if (tokenSegs[yIdx].token === 'yy' && !isPlaceholder(yv)) {
                    const full = yyToYYYY(Number(yv));
                    if (Number.isFinite(full)) yyyy = String(full);
                }

                if (!isPlaceholder(yyyy) && !monthOrDayValidator(dd, MM, yyyy)) {
                    // restore previous
                    parts[idx] = Number.isFinite(curNum)
                        ? String(curNum).padStart(width, '0')
                        : String(min).padStart(width, '0');
                    this.setViewFromTokenParts(parts);
                    this.focusTokenSeg(seg);
                    return;
                }
            }
        }

        this.setViewFromTokenParts(parts);

        if (nextNum === max) {
            const nxt = this.nextTokenSeg(seg);
            if (nxt) this.focusTokenSeg(nxt);
        } else {
            this.focusTokenSeg(seg);
        }
    }

    private deleteToken(seg: TokenSeg): void {
        const parts = this.getTokenPartsFromView();
        const idx = this.tokenSegments().indexOf(seg);
        if (idx < 0) return;

        parts[idx] = seg.token; // placeholder
        this.setViewFromTokenParts(parts);
        this.focusTokenSeg(seg);
    }

    private setNow(): void {
        const d = new Date();

        const yyyy = String(d.getFullYear()).padStart(4, '0');
        const MM = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');

        // always uses 24h for model; view token decides HH vs hh
        const HH = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');

        const hasDate = this.hasDate();
        const hasTime = this.hasTime();

        let model: string;
        if (hasDate && hasTime) model = `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}`;
        else if (hasDate) model = `${yyyy}-${MM}-${dd}`;
        else model = `${HH}:${mm}:${ss}`;

        this.modelValue.set(model);
        const view = this.formatModelToView(model);
        this.inputElement.value = view;
        this.viewValue.set(view);
        this.emitModelFromView();
    }

    // -----------------------------
    // DOM listeners
    // -----------------------------

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
    }

    @HostListener('keydown', ['$event'])
    handleKeydown(event: KeyboardEvent): boolean {
        if (event.key === 'Tab' || event.key === 'Enter') return true;
        if (!this.ready()) return true;

        const key = event.key;

        // SPACE = now, even if caret token not detectable
        if (key === ' ') {
            event.preventDefault();
            this.setNow();
            const first = this.tokenSegments()[0];
            if (first) this.focusTokenSeg(first);
            return false;
        }

        const seg = this.getTokenSegAtCaret();
        if (!seg) return true;

        if (key === 'ArrowLeft') {
            event.preventDefault();
            const prev = this.nextTokenSeg(seg, true);
            if (prev) this.focusTokenSeg(prev);
            return false;
        }

        if (key === 'ArrowRight') {
            event.preventDefault();
            const next = this.nextTokenSeg(seg, false);
            if (next) this.focusTokenSeg(next);
            return false;
        }

        if (key === 'ArrowUp') {
            event.preventDefault();
            this.bumpToken(seg, +1);
            this.emitModelFromView();
            return false;
        }

        if (key === 'ArrowDown') {
            event.preventDefault();
            this.bumpToken(seg, -1);
            this.emitModelFromView();
            return false;
        }

        if (key === 'Backspace' || key === 'Delete') {
            event.preventDefault();
            this.deleteToken(seg);
            this.emitModelFromView();
            return false;
        }

        if (isDigit(key)) {
            event.preventDefault();
            this.writeDigit(seg, key);
            this.emitModelFromView();
            return false;
        }

        // block everything else
        event.preventDefault();
        return false;
    }
}
